import { useEffect, useCallback, useRef, memo, FC } from "react";
import { Platform } from "react-native";
import { GestureDetector } from "react-native-gesture-handler";
import { View, Text } from "@src/components";
import { WeeklyAlarmRoutine } from "@visioni/a3s-alarm-schedules/schema";
import { type DayOfTheWeek } from "@visioni/schedules";
import { convertGridStateToWeeklyRoutine, parseWeeklyRoutineToGridState } from "./utils";
import { useDragSelection } from "../../../hooks/useDragSelection";
import { useWebDragSelection } from "../../../hooks/useWebDragSelection";
import { DayColumn } from "../DayColumn";
import {
  type Granularity,
  DEFAULT_GRANULARITY,
  getSlotsPerDay,
  generateSlots,
  formatSlotTime,
  detectRoutineGranularity,
} from "../granularity-utils";
import { createGridStore, useGranularity, type GridStore } from "../grid-store";
import { StyleSheet } from "react-native-unistyles";
import { CELL_HEIGHT, CELL_MARGIN_BOTTOM, CELL_TOTAL } from "../constants";

interface WeeklyRoutineGridProps {
  onDataChange: (routine: WeeklyAlarmRoutine) => void;
  initialData?: WeeklyAlarmRoutine;
  store: GridStore;
}

const DAYS: DayOfTheWeek[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

/**
 * Calculate the initial granularity for the grid based on the provided routine data.
 * If no initial data is provided, returns the default granularity of 60 minutes.
 */
function getInitialGranularity(initialData?: WeeklyAlarmRoutine): Granularity {
  if (!initialData) return DEFAULT_GRANULARITY;
  return detectRoutineGranularity(initialData);
}

export function createWeeklyStore(initialData?: WeeklyAlarmRoutine): GridStore {
  const initialGranularity = getInitialGranularity(initialData);
  return createGridStore(
    initialData ? parseWeeklyRoutineToGridState(initialData, initialGranularity) : {},
    getSlotsPerDay(initialGranularity),
    initialGranularity,
  );
}

// Static day headers — extracted so they never re-render on granularity switch
const WeeklyDayHeaders = memo(() => (
  <View flexDirection="row" mb="md">
    <View width={42} />
    {DAYS.map((day, index) => (
      <View key={day} flex={1} alignItems="center">
        <Text textStyle="sm/regular" color="textMuted">
          {DAY_LABELS[index]}
        </Text>
      </View>
    ))}
  </View>
));

export const WeeklyRoutineGrid: FC<WeeklyRoutineGridProps> = memo(({ onDataChange, initialData, store }) => {
  // Granularity lives in the store — this subscription triggers a re-render
  // only when granularity changes, which is needed for time labels.
  const granularity = useGranularity(store) as Granularity;

  // Ref for onDataChange needed in store subscription
  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Subscribe to store changes — converts grid state to routine and notifies parent.
  // Runs outside React's render cycle so the grid doesn't re-render on cell toggles.
  // Reads granularity directly from the store state (not a ref).
  useEffect(() => {
    return store.subscribe((state) => {
      const routine = convertGridStateToWeeklyRoutine(state.cells, state.granularity as Granularity);
      onDataChangeRef.current(routine);
    });
  }, [store]);

  // Initial conversion + re-convert on granularity changes
  useEffect(() => {
    const routine = convertGridStateToWeeklyRoutine(store.getState().cells, granularity);
    onDataChangeRef.current(routine);
  }, [granularity, store]);

  const handleDragSelection = useCallback(
    (selections: Array<{ day: number | string; hour: number }>) => {
      if (selections.length === 0) return;
      const { cells, batchUpdate } = store.getState();
      const firstKey = `${selections[0].day}-${selections[0].hour}`;
      const shouldSelect = !cells[firstKey];
      const updates: Record<string, boolean> = {};
      selections.forEach(({ day, hour }) => {
        updates[`${day}-${hour}`] = shouldSelect;
      });
      batchUpdate(updates);
    },
    [store],
  );

  // Use platform-specific drag selection hooks — drag state lives in the
  // Zustand store so individual cells subscribe to their own drag status.
  const mobileDragSelection = useDragSelection({
    onSelectionChange: handleDragSelection,
    store,
  });

  const getGridCells = useCallback(() => store.getState().cells, [store]);
  const webDragSelection = useWebDragSelection({
    onSelectionChange: handleDragSelection,
    getGridState: getGridCells,
    store,
  });

  const { registerCell, containerRef } = Platform.OS === "web" ? webDragSelection : mobileDragSelection;

  const panGesture = Platform.OS === "web" ? undefined : mobileDragSelection.panGesture;
  const containerProps = Platform.OS === "web" ? webDragSelection.getContainerProps() : {};

  const toggleCell = useCallback(
    (day: number | string, slot: number) => {
      store.getState().toggleCell(`${day}-${slot}`);
    },
    [store],
  );

  // Update grid state when initialData changes
  useEffect(() => {
    if (initialData) {
      const currentGranularity = store.getState().granularity as Granularity;
      const requiredGranularity = detectRoutineGranularity(initialData);

      // Parse new initialData with the required granularity
      const newCells = parseWeeklyRoutineToGridState(initialData, requiredGranularity);

      // If the new routine requires a different granularity, update it along with slotsPerDay
      if (requiredGranularity !== currentGranularity) {
        store.setState({
          granularity: requiredGranularity,
          slotsPerDay: getSlotsPerDay(requiredGranularity),
          cells: newCells,
          initialCells: newCells,
        });
      } else {
        store.setState({ cells: newCells, initialCells: newCells });
      }
    }
  }, [initialData, store]);

  const gridContent = (
    <View
      pt="xl"
      width="100%"
      ref={Platform.OS === "web" ? undefined : (containerRef as React.Ref<never>)}
      {...containerProps}
    >
      <WeeklyDayHeaders />

      {/* Grid: time labels column + day columns */}
      <View flexDirection="row">
        {/* Time labels column — outside overflow container so first label isn't clipped */}
        <View width={42}>
          {generateSlots(granularity).map((slot) => (
            <View key={slot} style={styles.timeLabel} justifyContent="flex-start" alignItems="flex-end">
              <Text textStyle="xs/regular" color="textMuted" style={{ textAlign: "right" }}>
                {formatSlotTime(slot, granularity)}
              </Text>
            </View>
          ))}
        </View>

        {/* Day columns — row height clips to current granularity without re-rendering */}
        <View
          style={[styles.gridRow, { height: getSlotsPerDay(granularity) * CELL_TOTAL, flex: 1, flexDirection: "row" }]}
        >
          {DAYS.map((day) => (
            <DayColumn key={day} day={day} onPress={toggleCell} registerCell={registerCell} store={store} />
          ))}
        </View>
      </View>

      {/* End-of-day label */}
      <View flexDirection="row" mt="xs">
        <View width={42} justifyContent="flex-start" alignItems="flex-end" style={{ paddingRight: 8 }}>
          <Text textStyle="xs/regular" color="textMuted" style={{ textAlign: "right", marginTop: -14 }}>
            {formatSlotTime(getSlotsPerDay(granularity), granularity)}
          </Text>
        </View>
        <View style={{ flex: 1 }} />
      </View>
    </View>
  );

  // Wrap with GestureDetector only on mobile
  if (Platform.OS === "web") {
    return gridContent;
  }

  return <GestureDetector gesture={panGesture!}>{gridContent}</GestureDetector>;
});

const styles = StyleSheet.create({
  gridRow: {
    overflow: "hidden",
  },
  timeLabel: {
    height: CELL_HEIGHT,
    marginBottom: CELL_MARGIN_BOTTOM,
    paddingRight: 8,
    top: -10,
  },
});
